/**
 * The root element must contain these children:
 *
 * - content (div) containing the ... well .. content, having class '.js_rc_moreBox__content'
 * - contentWrapper (div) containing the content element, having class '.js_rc_moreBox__contentWrapper'
 * - button (button) for expanding/collapsing the more box, must have class '.js_rc_moreBoxButton'
 *
 * Root Element CSS Styles:
 * - rc_moreBox--collapsed assigned when collapsed
 * - rc_moreBox--hasMore assigned when content is expandable
 *
 * Root Element Attributes:
 * - data-scrollto: css-selector of element to scroll to when collapsing
 *
 * Example:
 * ```html
 * <div class="rc_moreBox js_rc_moreBox">
 *   <div class="js_rc_moreBox__contentWrapper rc_moreBox__contentWrapper rc_moreBox__contentWrapper--largeGradient">
 *     <div class="rc_moreBox__content js_rc_moreBox__content" lang="de">
 *       <div class="rc_details js_rc_details">
 *         This is the long, long, long content that can be hidden/shown by the use of the more box.
 *       </div>
 *     </div>
 *   </div>
 *   <div class="rc_moreBox__buttonWrapper">
 *     <button class="rc_moreBox__button js_rc_moreBoxButton p_btn100--2nd" data-expand="Mehr anzeigen" data-collapse="Weniger anzeigen" data-qa="showMore"></button>
 *   </div>
 * </div>
 * ```
 */
export class MoreBox {
    private _hasMore: boolean;
    private readonly contentWrapperElement: HTMLElement;
    private readonly contentElement: HTMLElement;

    constructor(
        private readonly rootElement: HTMLElement,
        private readonly initialContentWrapperHeight: number,
        private readonly startExpanded?: boolean
    ) {
        this._hasMore = false;

        const buttonElement = rootElement.querySelector('.js_rc_moreBoxButton')!;
        this.contentWrapperElement = rootElement.querySelector('.js_rc_moreBox__contentWrapper')! as HTMLElement;
        this.contentElement = rootElement.querySelector('.js_rc_moreBox__content')! as HTMLElement;

        if (buttonElement) {
            buttonElement.addEventListener('click', () => this.moreLinkClickHandler(), false);
        }

        this.initialize();
    }

    private initialize(): void {
        const contentElementHeight = this.getCurrentContentElementHeight();

        if (this.initialContentWrapperHeight + 5 < contentElementHeight) {
            if (!this._hasMore) {
                if (this.startExpanded) {
                    this.rootElement.classList.add('rc_moreBox--hasMore');
                    this.contentWrapperElement.style.height = 'auto';
                } else {
                    this.rootElement.classList.add('rc_moreBox--hasMore', 'rc_moreBox--collapsed');
                    this.contentWrapperElement.style.height = this.initialContentWrapperHeight + 'px';
                }
                this._hasMore = true;
            }
        } else if (this._hasMore) {
            this.rootElement.classList.remove('rc_moreBox--hasMore', 'rc_moreBox--collapsed');
            if (this.contentWrapperElement) {
                this.contentWrapperElement.style.height = 'auto';
            }
            this._hasMore = false;
        }
    }

    private getNewHeight(from: number, to: number, currentState: number): string {
        return from - (from - to) * currentState + 'px';
    }

    private animateHeight(
        element: HTMLElement,
        from: number,
        to: number,
        setToAutoWhenFinished: boolean,
        duration: number,
        startTime?: number
    ): void {
        const current = new Date().getTime();

        // Calculate the current status (in percent - interval [0,1]) of the animation.
        startTime = !startTime ? current : startTime;
        const currentState = (current - startTime) / duration;

        // Abort if animation is finished.
        if (currentState > 1) {
            if (setToAutoWhenFinished) {
                element.style.height = 'auto';
            } else {
                element.style.height = to + 'px';
            }
            return;
        }

        // Update height and current process of the whole animation.
        element.style.height = this.getNewHeight(from, to, currentState);

        // Register timeout for next animation step.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment

        if (window.o_global !== undefined) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            window.o_global?.helper?.requestAnimFrame(() => {
                this.animateHeight(element, from, to, setToAutoWhenFinished, duration, startTime);
            });
        } else {
            window.requestAnimationFrame(() => {
                this.animateHeight(element, from, to, setToAutoWhenFinished, duration, startTime);
            });
        }
    }

    isExpanded(): boolean {
        return !this.rootElement.classList.contains('rc_moreBox--collapsed');
    }

    getCurrentContentElementHeight(): number {
        const boundingClientRect = this.contentElement.getBoundingClientRect();
        return boundingClientRect.bottom - boundingClientRect.top;
    }

    private collapse(): void {
        this.animateHeight(this.contentWrapperElement, this.getCurrentContentElementHeight(), this.initialContentWrapperHeight, false, 500);
    }

    private expand(): void {
        this.animateHeight(this.contentWrapperElement, this.initialContentWrapperHeight, this.getCurrentContentElementHeight(), true, 500);
    }

    private moreLinkClickHandler(): void {
        if (this._hasMore) {
            const isExpanded = this.isExpanded();
            if (isExpanded) {
                this.rootElement.classList.add('rc_moreBox--collapsed');
                this.collapse();
            } else {
                this.rootElement.classList.remove('rc_moreBox--collapsed');
                this.expand();
            }
        }
    }
}
